import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Badge, Button, Dropdown } from "antd";
import { BellOutlined, UserOutlined } from "@ant-design/icons";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";

import { db } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { APP_BRAND_NAME } from "../lib/brand";
import { DIT_LOGO_ALT, DIT_LOGO_URL } from "../lib/dit";
import NotificationsPanel, { type NotificationDoc } from "./NotificationsPanel";

const NAV_ITEMS_OPERATOR = [
  { to: "/", label: "Dashboard" },
  { to: "/projects", label: "Projects" },
  { to: "/intakes", label: "Intakes" },
  { to: "/board", label: "Board" },
  { to: "/vendors", label: "Vendors" },
  { to: "/settings", label: "Settings" },
];

// OAIP-48: executive role gets a read-only set focused on the exec dashboard
// and lookup pages. No "Dashboard" or "Board" since those are operator views.
const NAV_ITEMS_EXEC = [
  { to: "/exec", label: "Executive" },
  { to: "/projects", label: "Projects" },
  { to: "/intakes", label: "Intakes" },
  { to: "/vendors", label: "Vendors" },
  { to: "/board", label: "Board" },
  { to: "/settings", label: "Settings" },
];

// Production hostname comes from VITE_APEX_PROD_HOST so the comparison
// stays a configurable exact match — not a hardcoded substring includes()
// (which CodeQL js/incomplete-url-substring-sanitization rightly flags
// as the same anti-pattern that bites real authorization checks).
const PROD_HOST = (import.meta.env.VITE_APEX_PROD_HOST || "").trim().toLowerCase();

function getEnvLabel(): string {
  if (typeof window === "undefined") return "production";
  const host = window.location.hostname.toLowerCase();
  if (host === "localhost" || host === "127.0.0.1") return "local";
  if (PROD_HOST && host === PROD_HOST) return "production";
  return host;
}

export default function TopNav() {
  const { user, signOutUser, isExec } = useAuth();
  const navItems = isExec ? NAV_ITEMS_EXEC : NAV_ITEMS_OPERATOR;
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [personal, setPersonal] = useState<NotificationDoc[]>([]);
  const [broadcast, setBroadcast] = useState<NotificationDoc[]>([]);

  const currentUserEmail = user?.email ?? null;

  useEffect(() => {
    if (!currentUserEmail) {
      setPersonal([]);
      setBroadcast([]);
      return;
    }
    const baseRef = collection(db, "notifications");
    const personalQuery = query(
      baseRef,
      where("userId", "==", currentUserEmail),
      where("read", "==", false),
      orderBy("createdAt", "desc")
    );
    const broadcastQuery = query(
      baseRef,
      where("userId", "==", "broadcast"),
      where("read", "==", false),
      orderBy("createdAt", "desc")
    );
    const unsubPersonal = onSnapshot(personalQuery, (snap) => {
      setPersonal(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<NotificationDoc, "id">) }))
      );
    });
    const unsubBroadcast = onSnapshot(broadcastQuery, (snap) => {
      setBroadcast(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<NotificationDoc, "id">) }))
      );
    });
    return () => {
      unsubPersonal();
      unsubBroadcast();
    };
  }, [currentUserEmail]);

  const unreadCount = useMemo(
    () => personal.length + broadcast.length,
    [personal, broadcast]
  );

  const env = getEnvLabel();
  const isProd = env === "production";
  const initials = (user?.email || "?")
    .split("@")[0]
    .split(/[._-]/)
    .map((p) => p.charAt(0).toUpperCase())
    .slice(0, 2)
    .join("");

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    // `/exec` must not light up "Executive" when the user is on a different
    // exact-prefix path (none today, but startsWith() would also match e.g.
    // `/exec-something`). Keep the same semantics as `/`.
    if (to === "/exec") return location.pathname === "/exec";
    return location.pathname.startsWith(to);
  };

  return (
    <header className="apex-topnav" role="banner">
      <div className="apex-topnav__row">
        <div className="apex-topnav__brand">
          <img
            src={DIT_LOGO_URL}
            alt={`${DIT_LOGO_ALT} logo`}
            className="apex-topnav__logo"
          />
          <div className="apex-topnav__wordmark">
            <span className="apex-topnav__wordmark-name">{APP_BRAND_NAME}</span>
            <span className="apex-topnav__wordmark-tagline">Office of AI &amp; Policy</span>
          </div>
        </div>

        <nav className="apex-topnav__nav" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={`apex-topnav__link${isActive(item.to) ? " is-active" : ""}`}
              end={item.to === "/" || item.to === "/exec"}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="apex-topnav__actions">
          {!isProd && (
            <span
              className="apex-topnav__env"
              aria-label={`Environment: ${env}`}
              title={`Environment: ${env}`}
            >
              <span
                className="apex-topnav__env-dot"
                style={{ background: env === "local" ? "#b48700" : "#1e79c8" }}
              />
              {env}
            </span>
          )}

          {user && (
            <>
              <Badge
                count={unreadCount}
                size="small"
                offset={[-4, 4]}
                data-testid="notifications-badge"
              >
                <button
                  type="button"
                  className="apex-topnav__icon-btn"
                  aria-label={`Open notifications (${unreadCount} unread)`}
                  onClick={() => setDrawerOpen(true)}
                >
                  <BellOutlined />
                </button>
              </Badge>
              <NotificationsPanel
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                currentUserEmail={currentUserEmail}
              />
            </>
          )}

          {user ? (
            <Dropdown
              trigger={["click"]}
              placement="bottomRight"
              menu={{
                items: [
                  {
                    key: "email",
                    label: (
                      <div style={{ padding: "4px 0", minWidth: 200 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>
                          {user.displayName || user.email}
                        </div>
                        {user.displayName && (
                          <div style={{ fontSize: 12, color: "#56657a" }}>{user.email}</div>
                        )}
                      </div>
                    ),
                    disabled: true,
                  },
                  { type: "divider" },
                  { key: "signout", label: "Sign out", onClick: signOutUser },
                ],
              }}
            >
              <button
                type="button"
                className="apex-topnav__icon-btn"
                aria-label="Open account menu"
                style={{
                  background: "rgba(9, 41, 64, 0.06)",
                  color: "#092940",
                  fontWeight: 600,
                  fontSize: 12,
                  letterSpacing: "0.02em",
                }}
              >
                {initials || <UserOutlined />}
              </button>
            </Dropdown>
          ) : (
            <Button onClick={() => navigate("/signin")} type="primary">
              Sign in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
